import React, { FormEvent, useCallback, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from '@emotion/styled';
import InlineMessage from '@atlaskit/inline-message';
import { gridSize } from '@atlaskit/theme';
import Button from '@atlaskit/button';
import OtpCodeField, { CodeType } from './OtpCodeField';
import TallButton from './TallButton';
import { MultifactorMethod, MultiFactorOTPError } from '../types';
import { SecurityWall } from './SecurityWall';
import {
  InlineMessageContainer,
  InputMessage,
  StyledMessage,
} from '../containers/MultiFactorPage.helpers';
import EmailSent from './EmailSent';
import MfaChallengeSecurityKey from './MfaChallengeSecurityKey/MfaChallengeSecurityKey';
import messages from './MultiFactorForm.messages';

const ButtonContainer = styled.div`
  margin-top: ${gridSize()}px;
`;

const ResendButton = styled(Button)`
  span {
    text-align: left;
    white-space: normal;
  }
`;

export type MultiFactorFormProps = {
  multifactorMethod: MultifactorMethod | undefined;
  phoneNumber?: string;
  email?: string;
  otpError?: MultiFactorOTPError;
  isLoading: boolean;
  verifyOtpCode?: (string: string) => void;
  resendOtp?: () => void;
  clearInputTrigger?: number; // eg. can be the timestamp of the trigger
};

const MultiFactorForm = ({
  email,
  isLoading,
  multifactorMethod,
  otpError,
  phoneNumber,
  resendOtp,
  verifyOtpCode,
  clearInputTrigger,
}: MultiFactorFormProps) => {
  const { formatMessage } = useIntl();

  const helpTooltip = (
    method: MultifactorMethod | undefined,
    phoneNumber?: string,
    email?: string
  ) => {
    if (method === 'sms') {
      const resendButton = (
        <ResendButton id="resend-sms" appearance="link" onClick={resendOtp} spacing="none">
          <FormattedMessage id="mf.mfform.sms.resend" defaultMessage="Resend the text message." />
        </ResendButton>
      );
      return (
        <FormattedMessage
          id="mf.mfform.getsms.tooltip"
          tagName="p"
          defaultMessage="We've sent a text message (SMS) with the verification code to {mobileNumber}. Didn't get it? {resendButton}"
          values={{ mobileNumber: <b>{phoneNumber}</b>, resendButton }}
        />
      );
    }
    if (method === 'email-otp') {
      const resendButton = (
        <ResendButton id="resend-email-otp" appearance="link" onClick={resendOtp} spacing="none">
          <FormattedMessage
            id="mf.mfform.emailotp.resend"
            defaultMessage="Resend the verification code."
          />
        </ResendButton>
      );
      return (
        <FormattedMessage
          id="mf.mfform.emailotp.tooltip"
          tagName="p"
          defaultMessage="We've sent the verification code to {email}. Didn't get it? {resendButton}"
          values={{ email: <b>{email}</b>, resendButton }}
        />
      );
    }
    if (method === 'security-key') {
      return (
        <FormattedMessage
          id="mf.mfform.securitykey.tooltip"
          tagName="p"
          defaultMessage="Use any security key you registered with Atlassian"
        />
      );
    }
    return (
      <FormattedMessage
        id="mf.mfform.getcode.tooltip"
        tagName="p"
        defaultMessage="Open the verification app on your phone to get your verification code."
      />
    );
  };

  const handleFormSubmit = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      const formData = new FormData(e.currentTarget);
      const otpCode = formData.get('otpCode')?.toString() ?? '';
      verifyOtpCode?.(otpCode);
    },
    [verifyOtpCode]
  );

  const otpPromptMessage = useMemo(() => {
    switch (multifactorMethod) {
      case 'sms':
        return <FormattedMessage {...messages.mfSmsPrompt} />;
      case 'otp':
        return <FormattedMessage {...messages.mfTotpPrompt} />;
      case 'email-otp':
        return <FormattedMessage {...messages.mfEmailPrompt} />;
      default:
        return (
          <FormattedMessage
            id="mf.mfform.getcode"
            defaultMessage="Enter your verification code to continue."
          />
        );
    }
  }, [multifactorMethod]);

  const renderOtpForm = (codeLength: 6 | 8 | undefined, codeType: CodeType) => {
    return (
      <form autoComplete="off" onSubmit={handleFormSubmit}>
        <InputMessage>
          {otpPromptMessage}
          <InlineMessageContainer data-testid="mfform-tooltip-icon">
            <InlineMessage
              appearance="info"
              placement="bottom-end"
              iconLabel={formatMessage({
                id: 'mf.mfform.getcode.tooltip.icon.label',
                defaultMessage: 'More info',
                description: 'Icon label for the help tooltip',
              })}>
              <StyledMessage>{helpTooltip(multifactorMethod, phoneNumber, email)}</StyledMessage>
            </InlineMessage>
          </InlineMessageContainer>
        </InputMessage>

        <OtpCodeField
          id="two-step-verification-otp-code-input"
          disabled={isLoading}
          fieldErrorFromParent={otpError}
          codeLength={codeLength}
          codeType={codeType}
          clearInputTrigger={clearInputTrigger}
        />

        <ButtonContainer id="two-step-verification-submit">
          <TallButton appearance="primary" type="submit" isFullWidth isLoading={isLoading}>
            <FormattedMessage id="common.login" defaultMessage="Log in" />
          </TallButton>
        </ButtonContainer>
      </form>
    );
  };

  if (multifactorMethod === 'email-otp') {
    return <EmailSent>{renderOtpForm(6, 'alphanumeric')}</EmailSent>;
  } else if (multifactorMethod === 'security-key') {
    return <MfaChallengeSecurityKey />;
  } else {
    const securityWallStyle = multifactorMethod === 'otp' ? 'totp' : 'default';
    return (
      <SecurityWall id="two-step-verification-form" securityWallStyle={securityWallStyle}>
        {renderOtpForm(6, 'numeric')}
      </SecurityWall>
    );
  }
};

export default MultiFactorForm;
